import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Turnstile } from '@marsidev/react-turnstile';
import type { Event } from '../types/Event';
import axios from '../api/axios';
import { describeCron } from '../utils/cronDescription';

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export type NewEventPayload = Omit<Event, 'id' | 'createdById' | 'createdAt'> & {
  email?: string | null;
};

interface AddEventModalProps {
  onSubmit: (newEvent: NewEventPayload) => Promise<void>;
  onCancel: () => void;
  isLoggedIn?: boolean;
}

interface EventModalFormData {
  free: boolean;
  recurring: boolean;
  recurrencePattern: string;
  recurrenceCron: string;
  recurrenceStartDate: string;
  recurrenceEndDate: string;
  title: string;
  description: string;
  location: string;
  url: string;
  startTime: string;
  endTime: string;
  email: string;
  ticketUrl: string;
  imageUrl: string;
}

// Component for handling map updates
const MapController = ({ 
  onLocationSelect, 
  onReverseGeocode,
  locationQuery 
}: { 
  onLocationSelect: (latlng: { lat: number; lng: number }) => void;
  onReverseGeocode: (address: string) => void;
  locationQuery: string;
}) => {
  const map = useMap();

  useMapEvents({
    click: async (e) => {
      onLocationSelect(e.latlng);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${e.latlng.lat}&lon=${e.latlng.lng}`
        );
        const data = await response.json();
        if (data && data.display_name) {
          onReverseGeocode(data.display_name);
        }
      } catch (error) {
        console.error('Reverse geocoding error:', error);
      }
    },
  });

  const debouncedLocation = useDebounce(locationQuery, 500);

  // Geocoding function
  const updateMapView = useCallback(async (query: string) => {
    if (!query) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      );
      const data = await response.json();

      if (data && data[0]) {
        const { lat, lon } = data[0];
        map.setView([parseFloat(lat), parseFloat(lon)], 13);
        onLocationSelect({ lat: parseFloat(lat), lng: parseFloat(lon) });
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
  }, [map, onLocationSelect]);

  useEffect(() => {
    if (debouncedLocation) {
      updateMapView(debouncedLocation);
    }
  }, [debouncedLocation, updateMapView]);

  return null;
};

export default function AddEventModal({ onSubmit, onCancel, isLoggedIn = true }: AddEventModalProps) {
  const openedAtRef = useRef(Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const paidEventsEnabled = import.meta.env.VITE_PAID_EVENTS_ENABLED === 'true';
  const { register, handleSubmit, formState: { errors }, control, setValue, getValues } = useForm<EventModalFormData>({ defaultValues: { free: true, recurring: false, recurrencePattern: '', recurrenceCron: '', recurrenceStartDate: '', recurrenceEndDate: '' } });
  const [submitted, setSubmitted] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [location, setLocation] = useState({ lat: 52.2297, lng: 21.0122 }); // Default location (Warsaw)
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const BANK_ACCOUNT_NUMBER = '85 1140 2004 0000 3002 8580 8391'; // Update bank account number for paid events

  const addDays = (datetimeLocal: string, days: number): string => {
    const [datePart, timePart] = datetimeLocal.split('T');
    const d = new Date(datePart);
    d.setDate(d.getDate() + days);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${timePart || '00:00'}`;
  };

  const [submissionId] = useState(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return 'EVT-' + Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  });
  
  // Watch the location field
  const locationQuery = useWatch({
    control,
    name: 'location',
    defaultValue: ''
  });

  const isFree = useWatch({
    control,
    name: 'free',
    defaultValue: true,
  });

  const isRecurring = useWatch({
    control,
    name: 'recurring',
    defaultValue: false,
  });

  const recurrenceCronValue = useWatch({
    control,
    name: 'recurrenceCron',
    defaultValue: '',
  });

  const recurrencePatternValue = useWatch({
    control,
    name: 'recurrencePattern',
    defaultValue: '',
  });

  const imageUrlValue = useWatch({
    control,
    name: 'imageUrl',
    defaultValue: '',
  });

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      setValue('imageUrl', '');
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTagAdd = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleSubmitForm = async (data: EventModalFormData) => {
    if (data.recurring && data.recurrenceCron && data.recurrencePattern) {
      alert('Podaj albo wyrażenie cron, albo słowny opis cyklu — nie oba naraz.');
      return;
    }
    try {
      setIsSubmitting(true);

      let imageUrl: string | null = data.imageUrl || null;
      if (image) {
        const formData = new FormData();
        formData.append('file', image);
        formData.append('type', isLoggedIn ? 'confirmed' : 'submission');
        const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
        if (isLoggedIn) headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
        const uploadRes = await axios.post('/upload', formData, { headers });
        imageUrl = uploadRes.data.url;
      }

      const recurrenceAnchorIso = data.recurring && data.recurrenceStartDate
        ? new Date(data.recurrenceStartDate).toISOString()
        : null;

      const eventData = {
        ...data,
        free: paidEventsEnabled ? data.free : true,
        recurring: data.recurring,
        recurrencePattern: data.recurring ? (data.recurrencePattern || null) : null,
        recurrenceCron: data.recurring ? (data.recurrenceCron || null) : null,
        recurrenceStartDate: recurrenceAnchorIso,
        recurrenceEndDate: data.recurring && data.recurrenceEndDate ? new Date(data.recurrenceEndDate).toISOString() : null,
        lat: location.lat,
        lng: location.lng,
        imageUrl,
        tags,
        startTime: data.recurring ? (recurrenceAnchorIso ?? new Date().toISOString()) : new Date(data.startTime).toISOString(),
        endTime: data.recurring ? (recurrenceAnchorIso ?? new Date().toISOString()) : new Date(data.endTime).toISOString(),
        email: !isLoggedIn ? (data.email || null) : undefined,
        ticketUrl: (paidEventsEnabled && !isFree) ? (data.ticketUrl || null) : null,
        submissionId: (paidEventsEnabled && !isFree) ? submissionId : null,
      };

      if (isLoggedIn) {
        await onSubmit(eventData as NewEventPayload);
      } else {
        await axios.post('/submitted-events', { ...eventData, cf_turnstile_token: turnstileToken });
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting event:', error);
      alert('Nie udało się przesłać zgłoszenia');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div
      id="add-event-modal"
      className="fixed top-0 left-0 z-[2147483646] flex h-screen w-screen items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Dodaj wydarzenie"
    >
      <div
        id="add-event-modal-overlay"
        className="absolute inset-0 bg-black/35"
        onClick={() => {
          if (Date.now() - openedAtRef.current < 260) {
            return;
          }
          onCancel();
        }}
        aria-hidden="true"
      />

      <div
        id="add-event-modal-panel"
        className="relative w-[min(92vw,369px)] max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          id="add-event-modal-close"
          type="button"
          onClick={onCancel}
          aria-label="Zamknij okno dodawania wydarzenia"
          className="modal-close-btn absolute top-[8px] right-[8px] z-[2147483647] inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>

        <h2 id="add-event-modal-submitted-title" className="text-2xl font-bold mb-6 text-slate-100 text-center">
          {isLoggedIn ? 'Dodaj nowe wydarzenie' : 'Zgłoś wydarzenie'}
        </h2>

        {submitted ? (
          <div id="add-event-modal-submitted" className="py-8 text-center space-y-4">
            <p className="text-green-400 text-lg font-semibold">Dziękujemy za zgłoszenie!</p>
            <p className="text-slate-300 text-sm">Twoje wydarzenie zostało przesłane do moderacji. Po weryfikacji pojawi się na mapie.</p>
            <button
              type="button"
              onClick={onCancel}
              className="btn-action btn-green btn-full"
            >
              Zamknij
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit(handleSubmitForm)} id="add-event-modal-form" className="space-y-4">
          <div id="add-event-modal-fields" className="space-y-3">
            <div id="add-event-title-container">
              <label className="modal-label">Tytuł:</label>
              <input
                type="text"
                {...register('title', { required: 'Tytuł jest wymagany' })}
                className="modal-input"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-400">{errors.title.message as string}</p>
              )}
            </div>

            <div id="add-event-description-container">
              <label className="modal-label">Opis:</label>
              <textarea
                {...register('description', {
                  required: 'Opis jest wymagany',
                  maxLength: { value: 500, message: 'Opis może mieć maksymalnie 500 znaków' },
                })}
                maxLength={500}
                rows={4}
                className="modal-textarea"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description.message as string}</p>
              )}
            </div>

            <div id="add-event-location-container">
              <label className="modal-label">Lokalizacja:</label>
              <input
                type="text"
                {...register('location', { required: 'Lokalizacja jest wymagana' })}
                className="modal-input"
              />
              {errors.location && (
                <p className="mt-1 text-sm text-red-400">{errors.location.message as string}</p>
              )}
            </div>

            <div id="add-event-map-container">
              <label className="modal-label">
                Lokalizacja (kliknij na mapę, aby wybrać):
              </label>
              <div id="add-event-map" className="h-[300px] rounded-lg overflow-hidden border border-white/20">
                <MapContainer
                  center={[location.lat, location.lng]}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution=""
                  />
                  <Marker position={[location.lat, location.lng]} />
                  <MapController 
                    onLocationSelect={setLocation}
                    onReverseGeocode={(address) => setValue('location', address)}
                    locationQuery={locationQuery}
                  />
                </MapContainer>
              </div>
            </div>

            <div id="recurring">
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  {...register('recurring')}
                />
                <span className="modal-label !mb-0">Wydarzenie cykliczne</span>
              </label>
            </div>

            <div id="add-event-time-container" className="flex flex-col gap-3">
              {!isRecurring && (
                <>
                  <div id="add-event-start-time-container">
                    <label className="modal-label">Czas rozpoczęcia:</label>
                    <input
                      id="add-event-start-time"
                      type="datetime-local"
                      {...register('startTime', {
                        required: !isRecurring ? 'Data rozpoczęcia jest wymagana' : false,
                        onChange: (e) => {
                          const et = getValues('endTime');
                          if (!et || e.target.value > et) setValue('endTime', addDays(e.target.value, 1));
                        },
                      })}
                      className="modal-input"
                    />
                    {errors.startTime && (
                      <p className="mt-1 text-sm text-red-400">{errors.startTime.message as string}</p>
                    )}
                  </div>

                  <div id="add-event-end-time-container">
                    <label className="modal-label">Czas zakończenia:</label>
                    <input
                    id="add-event-end-time"
                      type="datetime-local"
                      {...register('endTime', {
                        required: !isRecurring ? 'Data zakończenia jest wymagana' : false,
                        onChange: (e) => {
                          const st = getValues('startTime');
                          if (!st || st > e.target.value) setValue('startTime', addDays(e.target.value, -1));
                        },
                      })}
                      className="modal-input"
                    />
                    {errors.endTime && (
                      <p className="mt-1 text-sm text-red-400">{errors.endTime.message as string}</p>
                    )}
                  </div>
                </>
              )}

              {isRecurring && (
                <div id="recurrence-details">
                  <label className="modal-label" htmlFor="add-event-recurrence-start-date">Cykl obowiązuje od:</label>
                  <input
                    id="add-event-recurrence-start-date"
                    type="datetime-local"
                    {...register('recurrenceStartDate', {
                      required: isRecurring ? 'Data rozpoczęcia cyklu jest wymagana' : false,
                    })}
                    className="modal-input"
                  />
                  {errors.recurrenceStartDate && (
                    <p className="mt-1 text-sm text-red-400">{errors.recurrenceStartDate.message as string}</p>
                  )}

                  <label className="modal-label mt-2" htmlFor="add-event-recurrence-end-date">Cykl obowiązuje do (opcjonalnie):</label>
                  <input
                    id="add-event-recurrence-end-date"
                    type="datetime-local"
                    {...register('recurrenceEndDate')}
                    className="modal-input"
                  />

                  <label className="modal-label mt-2" htmlFor="add-event-recurrence-cron">Wyrażenie cron (opcjonalnie):</label>
                  <input
                    id="add-event-recurrence-cron"
                    type="text"
                    disabled={Boolean(recurrencePatternValue)}
                    {...register('recurrenceCron')}
                    placeholder="np. 0 9 * * 6"
                    className="modal-input disabled:opacity-50"
                  />
                  {describeCron(recurrenceCronValue) && (
                    <p className="mt-1 text-xs text-slate-300">{describeCron(recurrenceCronValue)}</p>
                  )}

                  <label className="modal-label mt-2" htmlFor="add-event-recurrence-pattern">Cykl (słowny opis, opcjonalnie):</label>
                  <input
                    id="add-event-recurrence-pattern"
                    type="text"
                    disabled={Boolean(recurrenceCronValue)}
                    {...register('recurrencePattern')}
                    placeholder="np. Każda sobota o godzinie 9.00"
                    className="modal-input disabled:opacity-50"
                  />
                  <p className="mt-1 text-xs text-slate-300">Podaj wyrażenie cron albo słowny opis — nie oba naraz.</p>
                </div>
              )}
            </div>

            <div id="add-event-url-container">
              <label className="modal-label">Strona wydarzenia:</label>
              <input
                type="text"
                {...register('url', {
                  pattern: {
                    // Scheme (https://) and www. are optional - a bare domain name is enough.
                    value: /^(https?:\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+([/?#]\S*)?$/i,
                    message: 'Nieprawidłowy adres URL',
                  },
                })}
                className="modal-input"
              />
              {errors.url && (
                <p className="mt-1 text-sm text-red-400">{errors.url.message as string}</p>
              )}
            </div>

            <div id="add-event-image-url-container">
              <label className="modal-label" htmlFor="add-event-image-url">URL do zdjęcia poglądowego:</label>
              <input
                id="add-event-image-url"
                type="url"
                {...register('imageUrl', {
                  onChange: (e) => {
                    if (e.target.value) {
                      setImage(null);
                      setImagePreview(null);
                    }
                  },
                })}
                placeholder="https://..."
                className="modal-input"
                disabled={!!image}
              />
            </div>

            <div id="add-event-image-file-container">
              <label className="modal-label">Zdjęcie poglądowe:</label>
              {/* Mutually exclusive with the image URL field above - only one image source is allowed. */}
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageChange}
                className="modal-input"
                disabled={!!imageUrlValue}
              />
              {imagePreview && (
                <div id="add-event-image-preview-container" className="relative mt-2 w-full h-24">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-24 object-cover rounded"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImage(null);
                      setImagePreview(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    aria-label="Usuń zdjęcie"
                    className="modal-close-btn absolute top-1 right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border text-white"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                      <line x1="1" y1="1" x2="9" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                      <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div id="add-event-tags-container">
              <label className="modal-label">Tagi:</label>
              <div id="add-event-tags-input-row" className="flex gap-2 mt-1">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTagAdd())}
                  className="modal-input flex-1 !mb-0"
                  placeholder="Dodaj tag i naciśnij Enter"
                />
              </div>
              <div id="add-event-tags-list" className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium glass-chip"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => setTags(tags.filter(t => t !== tag))}
                      className="ml-1 opacity-70 hover:opacity-100"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div id="isFree">
              <label className="modal-checkbox-label">
                <input
                  type="checkbox"
                  {...register('free')}
                  disabled={!paidEventsEnabled}
                />
                <span className="modal-label !mb-0">Wstęp wolny</span>
              </label>
            </div>

            {paidEventsEnabled && !isFree && (
              <div id="add-event-paid-info-container" className="rounded-lg border border-orange-400/50 bg-orange-500/10 p-3 space-y-3">
                <p className="text-orange-300 text-xs font-semibold">
                  ⚠️ Opłata za dodanie wydarzenia biletowanego wynosi <strong>100 zł</strong>.
                </p>
                <div id="add-event-submission-details" className="rounded bg-white/10 p-2.5">
                  <p className="modal-label !mb-1">ID zgłoszenia</p>
                  <p className="font-mono text-white font-bold text-sm tracking-widest">{submissionId}</p>
                  <p className="modal-label !mb-1">Numer konta bankowego</p>
                  <p className="font-mono text-white font-bold text-sm tracking-widest">{BANK_ACCOUNT_NUMBER}</p>
                  <p className="text-slate-400 text-xs mt-1">Podaj to ID w tytule przelewu przy opłacaniu zgłoszenia.</p>
                </div>
                <div id="add-event-ticket-url-container">
                  <label className="modal-label">Link do biletów:</label>
                  <input
                    type="url"
                    {...register('ticketUrl')}
                    className="modal-input"
                    placeholder="https://bilety.pl/..."
                  />
                </div>
              </div>
            )}

            {!isLoggedIn && (
              <div id="add-event-email-container">
                <label className="modal-label">Adres email:</label>
                <input
                  type="email"
                  {...register('email', {
                    required: 'Adres email jest wymagany',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Podaj poprawny adres email',
                    },
                  })}
                  className="modal-input"
                  placeholder="twoj@email.pl"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-400">{errors.email.message as string}</p>
                )}
              </div>
            )}

            {!isLoggedIn && (
              <div id="add-event-turnstile-container" className="flex justify-center">
                <Turnstile
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA'}
                  onSuccess={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                  options={{ theme: 'dark' }}
                />
              </div>
            )}
          </div>

          <div id="add-event-modal-actions" className="modal-btn-row">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="btn-action btn-red"
            >
              Odrzuć
            </button>
            <button
              type="submit"
              disabled={isSubmitting || (!isLoggedIn && !turnstileToken)}
              className="btn-action btn-green"
            >
              {isSubmitting ? 'Zapisywanie...' : (isLoggedIn ? 'Zapisz' : 'Wyślij')}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );

  return modalContent;
}
